import { IMGS_PATH } from 'constants/image';
import { formatISO } from 'date-fns';
import Head from 'next/head';
import config from 'config';
import { getAuthorsBaseSlug } from 'data/utils/author';
import { useTranslation } from 'hooks/useTranslation';
import type { BlogItem } from 'types/Blog';

type Props = {
  entry: BlogItem;
};

const StructuredData = ({ entry }: Props): JSX.Element => {
  const { locale, t } = useTranslation();
  const authorsBaseSlug = getAuthorsBaseSlug(locale);
  const { author } = entry;
  const { production } = config.app.domains.proxy;
  const baseUrl = `https://${production}/${locale}`;

  const editorialPolicy = ((locale) => {
    switch (locale) {
      case 'en':
        return 'editorial-policy';
      case 'de':
        return 'redaktionelle-richtlinien';
      case 'nl':
        return 'redactioneel-beleid';
      case 'fr':
        return 'politique-editoriale';
      case 'es':
        return 'politica-editorial';
      case 'it':
        return 'politica-editoriale';
    }
  })(locale);

  const authorSchema = {
    author: {
      '@type': 'Person',
      name: author?.name,
      description: author?.metaDescription,
      jobTitle: author?.jobTitle,
      knowsAbout: author?.knowsAbout,
    },
  };

  const baseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: entry.title,
    datePublished: entry.publishDate
      ? formatISO(new Date(entry.publishDate as string))
      : entry.sys.firstPublishedAt,
    ...(author && authorSchema),
    dateModified: entry.sys.publishedAt,
    description: entry.metaDescription,
    mainEntityOfPage: `${baseUrl}/blog/${entry.slug}`,
    image: entry.bannerImage?.url || '',
    publisher: {
      '@type': 'Organization',
      name: t('qunomedical'),
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: `https://${production}${IMGS_PATH.PUBLIC}/qunomedical-logo.svg`,
        width: '494',
        height: '71',
      },
      publishingPrinciples: `${baseUrl}/${authorsBaseSlug}/${editorialPolicy}`,
      brand: {
        '@type': 'Thing',
        name: t('qunomedical'),
        sameAs: [
          'https://www.linkedin.com/company/qunomedical',
          'https://www.instagram.com/qunomedical/',
          'https://twitter.com/qunomedical',
          'https://www.facebook.com/qunomedical/',
        ],
      },
    },
  };

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(baseSchema) }}
      />
    </Head>
  );
};

export default StructuredData;
